import { useState, useRef, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiCheckUsername, apiUpdateProfile } from "../../services/authService";
import { isValidUsername } from "../../utils/validation";
import "../../App.css";

function formatMemberSince(dateStr) {
  if (!dateStr) return "—";
  // timeZone: "UTC" so the calendar date shown matches the stored UTC
  // timestamp exactly, regardless of the viewer's local timezone offset.
  return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}

export default function AccountProfileSection({ user, profile }) {
  const { updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [usernameInput, setUsernameInput] = useState(user.username ?? "");
  const [usernameStatus, setUsernameStatus] = useState("idle"); // idle|checking|available|taken|invalid
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const debounceRef = useRef(null);

  const checkUsername = useCallback((value) => {
    clearTimeout(debounceRef.current);
    if (!value || value === user.username) { setUsernameStatus("idle"); return; }
    if (!isValidUsername(value)) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    debounceRef.current = setTimeout(async () => {
      try {
        const { available } = await apiCheckUsername(value);
        setUsernameStatus(available ? "available" : "taken");
      } catch {
        setUsernameStatus("idle");
      }
    }, 400);
  }, [user.username]);

  function handleChange(e) {
    const val = e.target.value;
    setUsernameInput(val);
    checkUsername(val);
  }

  function startEditing() {
    setUsernameInput(user.username ?? "");
    setUsernameStatus("idle");
    setSaveError("");
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setSaveError("");
  }

  async function handleSave() {
    if (usernameInput === user.username) { setEditing(false); return; }
    if (!isValidUsername(usernameInput) || usernameStatus === "taken") return;
    setSaving(true);
    setSaveError("");
    try {
      const updated = await apiUpdateProfile(usernameInput);
      updateUser({ username: updated.username });
      setEditing(false);
    } catch (err) {
      setSaveError(err.message || "Could not update username.");
    } finally {
      setSaving(false);
    }
  }

  const usernameHint = {
    idle: null,
    checking: <span className="username-hint checking">Checking…</span>,
    available: <span className="username-hint available">✓ Available</span>,
    taken: <span className="username-hint taken">✗ Already taken</span>,
    invalid: <span className="username-hint invalid">3–20 chars, letters/numbers/_ /-</span>,
  }[usernameStatus];

  const saveDisabled = saving || usernameStatus === "taken" || usernameStatus === "invalid" || !usernameInput;
  const initial = (user.username ?? user.email ?? "?").charAt(0).toUpperCase();

  return (
    <div className="account-card account-profile-card">
      <div className="account-avatar">{initial}</div>
      <div className="account-profile-info">
        {editing ? (
          <div className="account-username-edit">
            <input
              className="auth-input"
              value={usernameInput}
              onChange={handleChange}
              maxLength={20}
              autoFocus
            />
            {usernameHint}
            {saveError && <p className="auth-error">{saveError}</p>}
            <div className="account-username-edit-actions">
              <button className="regen-btn" onClick={handleSave} disabled={saveDisabled}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button className="account-cancel-link" onClick={cancelEditing} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="account-profile-row">
            <h3 className="account-username">{user.username}</h3>
            <button className="account-edit-pencil" onClick={startEditing} aria-label="Edit username">✎</button>
          </div>
        )}
        <p className="account-email">{user.email}</p>
        <p className="account-member-since">Member since {formatMemberSince(profile?.createdAt)}</p>
      </div>
    </div>
  );
}
